import React, { type JSX } from 'react';
import { CustomTable, ConfirmModal, Hierarchy, CustomTransComponent, CustomIcon } from 'components';
import { Delete, Search, Warning as WarningIcon } from '@mui/icons-material';
import { Box, Chip, Skeleton, Typography, Stack, IconButton, Checkbox, Switch, Paper } from '@mui/material';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';
import * as Colors from '@brightlayer-ui/colors';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTypedSelector } from '@fiji/common';
import { useDispatch } from 'react-redux';
import OfflineBoltOutlinedIcon from '@mui/icons-material/OfflineBoltOutlined';
import {
    useGetLoadBalancingAutomationsWithFilterQuery,
    useDeleteLoadBalancingAutomationsMutation,
    useUpdateLoadBalancingAutomationAccessMutation,
} from '@fiji/common/src/features/loadBalancing/loadBalancingApi';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useDebounce, useIsMount, useRBAC } from '../../hooks';
import { useFilteredValues } from '../../hooks/useFilteredValues';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { GroupHierarchySkeleton } from '../GroupHierarchy/GroupHierarchySkeleton';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

type LoadBalancingTableProps = {
    isRampShedInProgress?: boolean;
};

export const LoadBalancingTable: React.FC<LoadBalancingTableProps> = ({ isRampShedInProgress = false }) => {
    const dispatch = useDispatch();
    const isMount = useIsMount();
    const tableRef = React.useRef<any>(undefined);
    const location = useLocation();
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canCreateLoadBalancing = hasPermission('load-balancing_create-devices');
    const canEditLoadBalancing = hasPermission('load-balancing_edit-devices');
    // const canViewLoadBalancing = hasPermission('load-balancing_view-devices');
    // const canViewLoadBalancingExpired = hasPermission('load-balancing_view-devices_hasExpired');
    const canDeleteLoadBalancing = hasPermission('load-balancing_delete-devices');

    const [payload, setPayload] = React.useState<any>({
        page: 0,
        size: 10,
        filters: { loadBalancing: true },
        sort: { key: 'isEnabled', sortType: 'ASC' },
        searchKey: '',
    });
    const handlePagination = (currentPage: number, pageSize: number): void => {
        setPayload((prev: any) => ({ ...prev, page: currentPage, size: pageSize }));
    };

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [searchKey, setSearchKey] = React.useState<string>('');
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);
    const [triggeredFilters, setTriggeredFilters] = React.useState<any>({});
    const [selectedGroups, setSelectedGroups] = React.useState<string[]>([]);
    const [groupsData, setGroupsData] = React.useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState<Record<string, any[]>>({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState<string[]>([]);
    const [getAllGroups, { data: groupsHierarchy }] = useGetAllGroupsMutation();

    const openMenu = Boolean(anchorEl);
    const navigate = useNavigate();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const currentOrg = useTypedSelector(selectedOrg);

    const {
        data: allAutomation,
        isLoading,
        isFetching,
    } = useGetLoadBalancingAutomationsWithFilterQuery(payload, {
        refetchOnMountOrArgChange: true,
        skip: !currentOrg?.id,
    });

    const [deleteAutomations, { isLoading: isDeleting }] = useDeleteLoadBalancingAutomationsMutation();
    const [updateAutomationAccess] = useUpdateLoadBalancingAutomationAccessMutation();

    const transformFilters = (filters: any): any => {
        const transformed = { ...filters };

        // Handle isEnabled boolean filter
        if (Object.prototype.hasOwnProperty.call(transformed, 'isEnabled')) {
            if (Array.isArray(transformed.isEnabled)) {
                const enabledValues = transformed.isEnabled.filter((v: any) => v !== 'all');

                if (enabledValues.length === 0 || enabledValues.length === 2) {
                    // No filter applied (show all)
                    transformed.isEnabled = undefined;
                } else if (enabledValues.length === 1) {
                    // Convert to boolean
                    transformed.isEnabled = enabledValues[0] === true || enabledValues[0] === 'true';
                }
            }
        }

        return transformed;
    };

    React.useEffect(() => {
        if (!isMount) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                searchKey: debouncedValue,
            }));
        }
    }, [debouncedValue]);

    React.useEffect(() => {
        if (currentOrg?.id) {
            void getAllGroups({});
        }
    }, [currentOrg]);

    React.useEffect(() => {
        if (!groupsData) {
            setGroupsData(groupsHierarchy?.data?.records);
        }
    }, [groupsHierarchy]);

    React.useEffect(() => {
        if (!isMount) {
            const filtersClone = { ...(payload?.filters ?? {}) };

            if (selectedGroups.length) {
                filtersClone.groups = selectedGroups;
            } else {
                delete filtersClone.groups;
            }

            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                filters: filtersClone,
            }));
        }
    }, [selectedGroups]);

    const handleFilterChange = (filters: any, sortData: any): void => {
        if (JSON.stringify(filters) !== JSON.stringify(triggeredFilters)) {
            setTriggeredFilters(filters);
        }
        if (sortData && Object.keys(sortData)?.length) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                sort: sortData,
            }));
        }
    };

    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
        if (isRampShedInProgress) return;
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (): void => {
        setAnchorEl(null);
    };

    const handleGetSelectedRow = (data: any): void => {
        if (Array.isArray(data)) {
            const ids = data.map((item) => item.id);
            setSelectedRows(ids);
        } else if (data?.id) {
            setSelectedRows((prev) => {
                if (prev.includes(data.id)) {
                    return prev.filter((id) => id !== data.id);
                }
                return [...prev, data.id];
            });
        }
    };

    const handleFetchHierarchy = async (parentData: any): Promise<void> => {
        setLoadingHierarchyNode((prevState): any => [...prevState, parentData?.id]);
        const { data: childHierarchyData }: any = await getAllGroups({ parent: parentData?.id });
        if (childHierarchyData) {
            setHierarchyMappingData((prevState) => ({
                ...prevState,
                [parentData?.id]: childHierarchyData?.data?.records,
            }));
        }

        setLoadingHierarchyNode((prevState): any => prevState.filter((loadingNode) => loadingNode !== parentData?.id));
    };

    const groupSelectionHandler = (id: string): void => {
        const payloadClone = JSON.parse(JSON.stringify(selectedGroups));
        const uncheckedIndex = payloadClone?.indexOf(id);
        if (uncheckedIndex !== -1) {
            payloadClone?.splice(uncheckedIndex, 1);
        } else if (id) {
            payloadClone?.push(id);
        }
        setSelectedGroups(payloadClone);
    };

    const collectAllGroupIds = (nodes: any[], mapping: Record<string, any[]>): string[] => {
        const ids: string[] = [];
        const hiddenTypes = ['DEVICE', 'GATEWAY'];
        const traverse = (items: any[]): void => {
            for (const item of items) {
                if (hiddenTypes.includes(item?.type)) continue;
                if (item?.id) ids.push(item.id);
                if (mapping[item?.id]) {
                    traverse(mapping[item.id]);
                }
            }
        };
        traverse(nodes);
        return ids;
    };

    const handleAllGroupsToggle = (): void => {
        const isCurrentlySelected = selectedGroups.includes(currentOrg?.id);
        if (isCurrentlySelected) {
            setSelectedGroups([]);
        } else {
            const allIds = collectAllGroupIds(groupsData ?? [], hierarchyMappingData);
            setSelectedGroups([currentOrg?.id, ...allIds]);
        }
    };

    const getGroupHeaderOptions = (): JSX.Element => (
        <>
            <Box
                sx={{
                    padding: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    ...(selectedGroups.includes(currentOrg?.id) && {
                        backgroundColor: 'rgb(224, 236, 245)',
                    }),
                }}
            >
                <Typography fontWeight={600}>
                    <CustomTransComponent translationKey={'COMMON:ALL_GROUPS'} />
                </Typography>
                <Checkbox checked={selectedGroups.includes(currentOrg?.id)} onChange={handleAllGroupsToggle} />
            </Box>

            <Hierarchy
                isLoading={!groupsData}
                data={groupsData ?? []}
                hasChild={(treeItem: any): boolean => treeItem?.groupCount}
                labelKey="name"
                hierarchyMappingData={hierarchyMappingData}
                loadingNode={loadingHierarchyNode}
                handleTreeNodeSelection={(node: any) => groupSelectionHandler(node?.id)}
                filters={[
                    {
                        key: 'type',
                        operator: '!==',
                        value: 'DEVICE',
                        action: 'hidden',
                    },
                    {
                        key: 'type',
                        operator: '!==',
                        value: 'GATEWAY',
                        action: 'hidden',
                    },
                ]}
                autoChildSelection
                loadMore={handleFetchHierarchy}
                loadingSkeleton={<GroupHierarchySkeleton row={1} />}
                selectedNodes={selectedGroups}
                selectionType="checkbox"
            />
        </>
    );

    const handleAddLoadAutomation = (): any => {
        navigate(`/${currentRealmName}/load-balancing`, {
            state: { mode: 'create', defaultTrigger: '', from: location.pathname + location.hash },
        });
    };

    const { t } = useTranslation();

    const handleConfirmDelete = async (): Promise<void> => {
        if (!selectedRows.length) return;
        try {
            await deleteAutomations({
                automationBalancingIds: selectedRows,
            }).unwrap();

            setSelectedRows([]);
            tableRef?.current?.resetCheckboxes();
            setIsDeleteModalOpen(false);
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:BALANCING.DELETE_SUCCESS_COUNT', {
                        replace: { count: selectedRows.length },
                    }),
                    type: 'success',
                })
            );
        } catch (error) {
            console.error('Failed to delete automations', error);
        }
    };

    const handleSwitchChange = async (row: any): Promise<void> => {
        if (isRampShedInProgress) return;
        try {
            await updateAutomationAccess({
                automationId: row.id,
                isEnabled: !row.isEnabled,
            }).unwrap();
        } catch (error) {
            console.error('Failed to update automation status', error);
        }
    };

    const handleOpenDeleteModal = (): void => {
        setIsDeleteModalOpen(true);
        handleMenuClose();
    };

    const handleChipDelete = (key: string): void => {
        tableRef?.current?.resetFilters(key, true);

        if (key === 'groups') {
            setSelectedGroups([]);
        }

        setTriggeredFilters((prev: any) => {
            const filtersClone = JSON.parse(JSON.stringify(prev ?? {}));
            delete filtersClone[key];
            return filtersClone;
        });

        setPayload((prev: any) => {
            const filtersClone = JSON.parse(JSON.stringify(prev?.filters ?? {}));
            delete filtersClone[key];

            return {
                ...prev,
                page: 0,
                filters: filtersClone,
            };
        });
    };

    const getChipName = (key: string): JSX.Element | string => {
        switch (key) {
            case 'name':
                return <CustomTransComponent translationKey={'COMMON:AUTOMATION_NAME'} />;
            case 'isEnabled':
                return <CustomTransComponent translationKey={'COMMON:ENABLED'} />;
            case 'devices':
                return <CustomTransComponent translationKey={'COMMON:DEVICE'} />;
            case 'groups':
                return <CustomTransComponent translationKey={'COMMON:GROUP'} />;
            case 'triggers':
                return <CustomTransComponent translationKey={'COMMON:TRIGGERS'} />;
            case 'action':
                return <CustomTransComponent translationKey={'COMMON:ACTIONS'} />;
            default:
                return key;
        }
    };

    const activeFilters = React.useMemo(() => {
        const filtersClone = { ...(payload?.filters ?? {}) };
        delete filtersClone.loadBalancing;
        return filtersClone;
    }, [payload?.filters]);

    // Handle row click to navigate to automation detail
    const handleRowNavigate = (row: any): void => {
        if (isRampShedInProgress) return;
        navigate(`/${currentRealmName}/load-balancing/${row.id}`, {
            state: { from: location.pathname + location.hash },
        });
    };

    function handleCreateAutomation(): void {
        navigate(`/${currentRealmName}/load-balancing`, {
            state: { mode: 'create', defaultTrigger: '', from: location.pathname + location.hash },
        });
        handleMenuClose();
    }

    const columns: any[] = React.useMemo(
        () => [
            {
                isSelectable: true,
                width: '10%',
                accessor: 'loadGroupsName',
                isSortable: true,
                cell: (row: any) =>
                    row.systemDisabled ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 42,
                                left: 14,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                whiteSpace: 'nowrap',
                                zIndex: 1,
                                pointerEvents: 'none',
                            }}
                        >
                            <WarningIcon
                                aria-label="system-disabled-warning"
                                sx={{ color: 'warning.main', fontSize: 16 }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'warning.main',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                }}
                            >
                                <CustomTransComponent translationKey={'WIDGETS:BALANCING.SYSTEM_DISABLED_WARNING'} />
                            </Typography>
                        </Box>
                    ) : null,
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: <CustomTransComponent translationKey={'COMMON:ENABLED'} />,
                width: '2%',
                accessor: 'isEnabled',
                isSortable: true,
                isFilterable: true,
                cell: (row: any): any => (
                    <Switch
                        id={row.id}
                        disabled={row.systemDisabled || isRampShedInProgress}
                        checked={row.isEnabled}
                        onChange={(e) => {
                            e.stopPropagation();
                            void handleSwitchChange(row);
                        }}
                    />
                ),
                filterOptions: [
                    { id: 'all', label: <CustomTransComponent translationKey={'SCHEDULING:ALL'} /> },
                    { id: 'true', label: <CustomTransComponent translationKey={'COMMON:ENABLED'} /> },
                    { id: 'false', label: <CustomTransComponent translationKey={'COMMON:DISABLED'} /> },
                ],
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: <CustomTransComponent translationKey={'COMMON:AUTOMATION_NAME'} />,
                width: '28%',
                accessor: 'name',
                isSortable: true,
                isFilterable: true,
                isDebounce: true,
                cell: (row: any) => (
                    <Stack spacing={0.5} sx={{ pb: row.systemDisabled ? '28px' : 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {row.name}
                        </Typography>
                    </Stack>
                ),
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: <CustomTransComponent translationKey={'COMMON:DEVICE'} />,
                width: '20%',
                accessor: 'devices',
                isSortable: true,
                isFilterable: true,
                isDebounce: true,
                paperWrapperClass: 'custom-search-bar-width',
                cell: (row: any): JSX.Element => {
                    const showLinkIcon = row.multipleDevice === true;
                    const showCustomIcon = !showLinkIcon && row.icon?.web;
                    return (
                        <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                                {row.loadGroup ?? '-'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {showLinkIcon && <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                                {showCustomIcon && (
                                    <CustomIcon
                                        family={row.icon.web.family}
                                        iconName={row.icon.web.name}
                                        iconProps={{ sx: { fontSize: 18, color: 'text.secondary' } }}
                                    />
                                )}
                                {row.deviceType && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Typography variant="caption" color="text.secondary">
                                            {row.device}
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                    );
                },
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: <CustomTransComponent translationKey={'COMMON:GROUP'} />,
                width: '20%',
                accessor: 'groups',
                isSortable: true,
                isFilterable: true,
                headerOptions: getGroupHeaderOptions,
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: <CustomTransComponent translationKey={'COMMON:TRIGGERS'} />,
                width: '20%',
                accessor: 'triggers',
                isSortable: true,
                isFilterable: true,
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
                paperWrapperClass: 'custom-search-bar-width',
            },
            {
                header: <CustomTransComponent translationKey={'COMMON:ACTIONS'} />,
                width: '15%',
                accessor: 'action',
                isSortable: true,
                isFilterable: true,
                paperWrapperClass: 'custom-search-bar-width',
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            ...(canEditLoadBalancing || canDeleteLoadBalancing || canCreateLoadBalancing
                ? [
                      {
                          width: '5%',
                          accessor: 'chevronAction',
                          cell: (row: any) => (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '80%' }}>
                                  <IconButton
                                      size="small"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          handleRowNavigate(row);
                                      }}
                                      disabled={isRampShedInProgress}
                                      sx={{
                                          color: '#727E84',
                                          cursor: isRampShedInProgress ? 'not-allowed' : 'pointer',
                                          '&.Mui-disabled': {
                                              cursor: 'not-allowed',
                                              pointerEvents: 'auto',
                                          },
                                      }}
                                  >
                                      <ChevronRightIcon />
                                  </IconButton>
                              </Box>
                          ),
                          extraOptions: () => (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                  <Button
                                      id="fade-button"
                                      aria-controls={openMenu ? 'fade-menu' : undefined}
                                      aria-haspopup="true"
                                      aria-expanded={openMenu ? 'true' : undefined}
                                      onClick={handleMenuClick}
                                      disabled={isRampShedInProgress}
                                      sx={{
                                          minWidth: 'auto',
                                          cursor: isRampShedInProgress ? 'not-allowed' : 'pointer',
                                          '&.Mui-disabled': {
                                              cursor: 'not-allowed',
                                              pointerEvents: 'auto',
                                          },
                                      }}
                                  >
                                      <MoreVertIcon sx={{ color: '#727E84' }} />
                                  </Button>
                                  <Menu
                                      id="fade-menu"
                                      slotProps={{
                                          list: {
                                              'aria-labelledby': 'fade-button',
                                          },
                                      }}
                                      slots={{ transition: Fade }}
                                      anchorEl={anchorEl}
                                      open={openMenu}
                                      onClose={handleMenuClose}
                                  >
                                      <MenuItem onClick={handleAddLoadAutomation}>
                                          <ListItemIcon>
                                              <AddIcon sx={{ color: '#727E84' }} fontSize="small" />
                                          </ListItemIcon>
                                          <ListItemText sx={{ fontSize: 16, fontWeight: 400, color: '#424e54' }}>
                                              <CustomTransComponent
                                                  translationKey={'WIDGETS:BALANCING.ADD_AUTOMATION'}
                                              />
                                          </ListItemText>
                                      </MenuItem>

                                      <MenuItem onClick={handleOpenDeleteModal} disabled={selectedRows.length === 0}>
                                          <ListItemIcon>
                                              <Delete
                                                  sx={{ color: selectedRows.length === 0 ? '#B0B8BC' : '#727E84' }}
                                                  fontSize="small"
                                              />
                                          </ListItemIcon>
                                          <ListItemText
                                              sx={{
                                                  fontSize: 16,
                                                  fontWeight: 400,
                                                  color: selectedRows.length === 0 ? '#B0B8BC' : '#424e54',
                                              }}
                                          >
                                              <CustomTransComponent
                                                  translationKey={'WIDGETS:BALANCING.DELETE_AUTOMATION'}
                                              />
                                          </ListItemText>
                                      </MenuItem>
                                  </Menu>
                              </Box>
                          ),
                      },
                  ]
                : []),
        ],
        [
            anchorEl,
            currentRealmName,
            isRampShedInProgress,
            openMenu,
            selectedRows,
            selectedGroups,
            groupsData,
            hierarchyMappingData,
            loadingHierarchyNode,
            t,
        ]
    );

    const [tableFilters] = useFilteredValues({
        allFilters: columns,
        triggeredFilters,
    });

    React.useEffect(() => {
        if (!isMount) {
            setPayload((prev: any) => {
                const transformedFilters = transformFilters(tableFilters);

                const newFilters: any = {
                    loadBalancing: true,
                };

                Object.keys(transformedFilters).forEach((key) => {
                    if (key !== 'loadBalancing') {
                        newFilters[key] = transformedFilters[key];
                    }
                });

                if (selectedGroups.length) {
                    newFilters.groups = selectedGroups;
                }

                return {
                    ...prev,
                    page: 0,
                    filters: newFilters,
                };
            });
        }
    }, [tableFilters, selectedGroups, isMount]);

    const hasSearchOrFilters = Object.keys(payload?.filters || {}).length > 1 || !!payload?.searchKey?.length;

    return (
        <>
            <Stack p={3}>
                <Stack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
                    <InputWithCustomPlaceholder
                        id="searchKey"
                        value={searchKey ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            setSearchKey(e.target.value);
                        }}
                        placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                        size="small"
                        InputProps={{
                            startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                        }}
                    />
                    {Object.keys(activeFilters).length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                            {Object.keys(activeFilters).map((key: string) => (
                                <Chip
                                    key={`load-balancing-filter-${key}`}
                                    label={getChipName(key)}
                                    onDelete={(): void => handleChipDelete(key)}
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>
            </Stack>
            <Paper className="box-shadow">
                <CustomTable
                    ref={tableRef}
                    isPagination={true}
                    keyToTraverse="id"
                    headers={columns}
                    handlePageChange={handlePagination}
                    handleFilterChange={handleFilterChange}
                    controlledPageSize={payload?.page}
                    data={allAutomation?.data?.records ?? []}
                    isLoading={isLoading || isFetching}
                    total={allAutomation?.data?.total ?? 0}
                    isWhiteRows={true}
                    noDataFoundTitle={t('WIDGETS:BALANCING.NO_LOAD_BALANCING_TITLE')}
                    noDataFoundDescription={
                        hasSearchOrFilters ? '' : t('WIDGETS:BALANCING.NO_LOAD_BALANCING_DESCRIPTION')
                    }
                    noDataFoundIcon={<OfflineBoltOutlinedIcon fontSize={'inherit'} sx={{ color: Colors.gray[500] }} />}
                    noDataFoundButtonText={hasSearchOrFilters ? '' : t('WIDGETS:BALANCING.CREATE_AUTOMATION')}
                    noDataFoundButtonAction={hasSearchOrFilters ? undefined : handleCreateAutomation}
                    getSelectedRow={handleGetSelectedRow}
                    getRowStyle={(row: any) => ({
                        backgroundColor: row.systemDisabled ? '#FFF4E5' : undefined,
                    })}
                />
            </Paper>
            <ConfirmModal
                isVisible={isDeleteModalOpen}
                header={t('WIDGETS:BALANCING.DELETE_AUTOMATIONS_HEADER')}
                description={t('WIDGETS:BALANCING.DELETE_AUTOMATIONS_DESCRIPTION_COUNT', {
                    replace: { count: selectedRows.length },
                })}
                actionButtonText={t('COMMON:DELETE')}
                isLoading={isDeleting}
                confirmClick={handleConfirmDelete}
                onCancel={() => setIsDeleteModalOpen(false)}
                buttonColor="error"
            />
        </>
    );
};
